const base = (accountId) => `account_id=${encodeURIComponent(accountId)}`;

// hasText: '' | 'yes' | 'no' — счётчики считаются с тем же фильтром, что и список
export const loadFeedbackStats = ({ request, accountId, hasText, kind }) =>
  request(`/api/v1/feedback/stats?${base(accountId)}${hasText ? `&has_text=${hasText === 'yes'}` : ''}${kind ? `&kind=${kind}` : ''}`);

export const getFeedbackItem = ({ request, itemId }) =>
  request(`/api/v1/feedback/items/${itemId}`);

export const loadFeedbackStatus = ({ request, accountId }) =>
  request(`/api/v1/feedback/status?${base(accountId)}`);

export const loadFeedbackSettings = ({ request, accountId }) =>
  request(`/api/v1/feedback/settings?${base(accountId)}`);

export const saveFeedbackSettings = ({ request, accountId, settings }) =>
  request(`/api/v1/feedback/settings?${base(accountId)}`, { method: 'PUT', body: JSON.stringify(settings) });

// status: waiting | draft | failed | moderation | sent | skipped | all (этапы обращения); hasText: '' | 'yes' | 'no'; rating: '' | '1'..'5'
export const loadFeedbackItems = async ({ request, accountId, status = 'waiting', kind, query, hasText, rating, reason, limit = 50, offset = 0 }) => {
  const params = new URLSearchParams({ account_id: accountId, limit: String(limit), offset: String(offset) });
  if (kind) params.set('kind', kind);
  if (query) params.set('q', query);
  if (hasText) params.set('has_text', hasText === 'yes' ? 'true' : 'false');
  if (rating) params.set('rating', rating);
  if (reason) params.set('reason', reason);
  if (status !== 'all') params.set('stage', status);
  return request(`/api/v1/feedback/items?${params.toString()}`);
};

// days и maxItems: разовая догрузка истории глубже обычного сбора
export const collectFeedbackNow = ({ request, accountId, days, maxItems }) => {
  const params = new URLSearchParams({ account_id: accountId });
  if (days) params.set('days', String(days));
  if (maxItems) params.set('max_items', String(maxItems));
  return request(`/api/v1/feedback/collect?${params.toString()}`, { method: 'POST' });
};

export const replyToFeedback = ({ request, itemId, text, send, dryRun }) =>
  request(`/api/v1/feedback/items/${itemId}/reply`, {
    method: 'POST',
    body: JSON.stringify({ text, send, dry_run: dryRun }),
  });

export const skipFeedback = ({ request, itemId }) =>
  request(`/api/v1/feedback/items/${itemId}/skip`, { method: 'POST' });

export const draftFeedbackReply = ({ request, itemId }) =>
  request(`/api/v1/feedback/items/${itemId}/draft`, { method: 'POST' });

export const previewFeedbackStyle = ({ request, accountId, style }) =>
  request(`/api/v1/feedback/preview?${base(accountId)}`, { method: 'POST', body: JSON.stringify({ style }) });

export const loadFeedbackUsage = ({ request, accountId, days = 30 }) =>
  request(`/api/v1/feedback/usage?${base(accountId)}&days=${days}`);

// База знаний о товаре
export const loadKnowledge = ({ request, accountId, query, onlyFilled, limit = 50, offset = 0 }) => {
  const params = new URLSearchParams({ account_id: accountId, limit: String(limit), offset: String(offset) });
  if (query) params.set('q', query);
  if (onlyFilled) params.set('only_filled', 'true');
  return request(`/api/v1/feedback/knowledge?${params.toString()}`);
};

export const getKnowledge = ({ request, accountId, sku }) =>
  request(`/api/v1/feedback/knowledge/${encodeURIComponent(sku)}?${base(accountId)}`);

export const saveKnowledge = ({ request, accountId, sku, customFacts, faq, proofLinks }) =>
  request(`/api/v1/feedback/knowledge/${encodeURIComponent(sku)}?${base(accountId)}`, {
    method: 'PUT',
    body: JSON.stringify({ custom_facts: customFacts, faq, ...(proofLinks ? { proof_links: proofLinks } : {}) }),
  });

// Анализ отзывов
// Период анализа: { days } (последние дни) или { from, to } (даты YYYY-MM-DD включительно)
const periodQuery = (period) => (period && period.from && period.to
  ? `&date_from=${period.from}&date_to=${period.to}`
  : `&days=${(period && period.days) || 90}`);

// filters: { ready: bool|undefined, q, sort, order, limit, offset }
export const loadAnalyticsProducts = ({ request, accountId, period, filters = {} }) => {
  const params = new URLSearchParams();
  if (filters.ready !== undefined && filters.ready !== null && filters.ready !== '') params.set('ready', String(filters.ready));
  if (filters.q) params.set('q', filters.q);
  ['sort', 'order', 'limit', 'offset'].forEach((key) => { if (filters[key] !== undefined) params.set(key, String(filters[key])); });
  return request(`/api/v1/feedback/analytics/products?${base(accountId)}${periodQuery(period)}&${params.toString()}`);
};

// kind: all | negative | positive
export const downloadFeedbackBase = ({ fetchBlob, accountId, format = 'jsonl' }) =>
  fetchBlob(`/api/v1/feedback/export?${base(accountId)}&format=${format}`);

export const loadProductReviews = ({ request, accountId, sku, period, kind = 'all', rating }) =>
  request(`/api/v1/feedback/analytics/products/${encodeURIComponent(sku)}/reviews?${base(accountId)}${periodQuery(period)}&kind=${kind}${rating ? `&rating=${rating}` : ''}`);

export const loadHistoryStatus = ({ request, accountId }) =>
  request(`/api/v1/feedback/history-status?${base(accountId)}`);

export const loadNegativeReviews = ({ request, accountId, sku, period }) =>
  request(`/api/v1/feedback/analytics/products/${encodeURIComponent(sku)}/negatives?${base(accountId)}${periodQuery(period)}`);

export const analyzeProduct = ({ request, accountId, sku, period }) =>
  request(`/api/v1/feedback/analytics/products/${encodeURIComponent(sku)}/analyze?${base(accountId)}${periodQuery(period)}`, { method: 'POST' });

export const analysisStatus = ({ request, accountId, sku }) =>
  request(`/api/v1/feedback/analytics/products/${encodeURIComponent(sku)}/analysis-status?${base(accountId)}`);

// since: YYYY-MM-DD; пусто — дата «собирать с» из настроек
export const collectFeedbackHistory = ({ request, accountId, since }) =>
  request(`/api/v1/feedback/collect-history?${base(accountId)}${since ? `&since=${since}` : ''}`, { method: 'POST' });

export const calculateReviews = ({ request, accountId, rating, count, target, star }) =>
  request(`/api/v1/feedback/analytics/calculator?${base(accountId)}&rating=${rating}&count=${count}&target=${target}&star=${star}`);

export const TONE_OPTIONS = [
  ['friendly', 'Дружелюбная'],
  ['business', 'Деловая'],
  ['expert', 'Экспертная'],
  ['caring', 'Заботливая'],
];
export const EMOJI_OPTIONS = [['none', 'Нет'], ['moderate', 'Умеренно (один)'], ['many', 'Да (1–3)']];
export const LENGTH_OPTIONS = [['short', 'Коротко'], ['normal', 'Обычно'], ['long', 'Развёрнуто']];
export const ADDRESS_OPTIONS = [['vy', 'На «вы»'], ['ty', 'На «ты»']];
export const ESCALATION_LABELS = {
  no_fact: 'нет нужного факта о товаре',
  risk: 'риск (здоровье, жалоба, суд)',
  claim: 'претензия или обвинение',
  price_or_stock: 'вопрос о цене, акции, наличии',
  unclear: 'непонятно, чего хочет покупатель',
  other: 'нужен человек',
};

export const REASON_LABELS = {
  no_text: 'Только оценка, без текста',
  already_answered: 'Уже есть ответ',
  superseded: 'Есть новое сообщение в чате',
  rule_authenticity: 'Правило: подлинность, отвечаю я',
  rule_claim: 'Правило: возврат или претензия, отвечаю я',
  rule_price_stock: 'Правило: цена или наличие, отвечаю я',
  rule_delivery: 'Правило: доставка, отвечаю я',
  rule_product: 'Правило: отвечаю я',
  rule_words: 'Правило: ваши слова, отвечаю я',
  hidden: 'Скрыт, ответ не виден',
  cancelled_order: 'Отменённый заказ',
  risk_stopword: 'Риск: юридическая или медицинская тема',
  claim_like_question: 'Похоже на претензию',
  question: 'Вопрос',
  chat: 'Чат с покупателем',
  support_open: 'Обращение в поддержку Ozon',
  negative_review: 'Негативный отзыв',
  short_praise: 'Короткая похвала',
  needs_review: 'Положительный, ответ ИИ',
  manual: 'Пропущено вручную',
  ai_no_fact: 'ИИ: нет нужного факта о товаре',
  ai_risk: 'ИИ: риск',
  ai_claim: 'ИИ: претензия',
  ai_price_or_stock: 'ИИ: цена, акция или наличие',
  ai_unclear: 'ИИ: непонятный запрос',
  ai_other: 'ИИ: нужен человек',
};

export const REPLY_STATUS_LABELS = {
  draft: 'Черновик',
  queued: 'В очереди у агента',
  moderation: 'Ответ отправлен, Ozon проверяет',
  sent: 'Отправлен',
  failed: 'Не отправлен',
};

export const FAIL_LABELS = {
  item_not_found: 'элемент не найден в списке кабинета',
  item_mismatch: 'текст карточки не совпал с ожидаемым',
  hidden: 'отзыв скрыт',
  reply_unavailable: 'на этот элемент ответ невозможен',
  reply_button_missing: 'в карточке нет кнопки отправки',
  reply_button_disabled: 'кнопка отправки неактивна',
  not_confirmed: 'агент не подтвердил отправку, проверьте в кабинете',
  lease_expired: 'агент не вернул результат',
  page_not_ready: 'страница кабинета не открылась (например, закрыт Chrome агента или баннер закрыл меню); отправлено не было, система повторит сама',
  seller_cdp_error: 'сбой в Chrome агента при работе с кабинетом, ответ мог не уйти: проверьте в кабинете и отправьте ещё раз',
  seller_extension_error: 'сбой расширения агента при работе с кабинетом, ответ мог не уйти: проверьте в кабинете и отправьте ещё раз',
  no_result: 'агент не прислал результат',
};

export const TEXT_PROBLEMS = {
  empty: 'пустой текст',
  too_long: 'длиннее 2000 символов',
  link: 'ссылки запрещены правилами площадки',
  phone: 'телефоны запрещены',
  messenger: 'мессенджеры запрещены',
  other_marketplace: 'упоминание других площадок',
  promise_money: 'обещание денег, возврата или подарка',
  ask_to_change_review: 'просьба изменить или удалить отзыв',
  forbidden_phrase: 'фраза из списка запрещённых магазином',
};

export const loadFeedbackBilling = ({ request, accountId, days = 30 }) =>
  request(`/api/v1/feedback/billing?${base(accountId)}&days=${days}`);

// База знаний по уровням (магазин, бренд, категория, модель)
export const loadKbGroups = ({ request, accountId, scope, query, limit = 50, offset = 0 }) => {
  const params = new URLSearchParams({ account_id: accountId, scope, limit: String(limit), offset: String(offset) });
  if (query) params.set("q", query);
  return request(`/api/v1/feedback/kb/groups?${params.toString()}`);
};

export const getKbEntry = ({ request, accountId, scope, key }) =>
  request(`/api/v1/feedback/kb/entry?${new URLSearchParams({ account_id: accountId, scope, key: key || "" }).toString()}`);

export const saveKbEntry = ({ request, accountId, scope, key, customFacts, faq }) =>
  request(`/api/v1/feedback/kb/entry?${new URLSearchParams({ account_id: accountId, scope, key: key || "" }).toString()}`, {
    method: "PUT", body: JSON.stringify({ custom_facts: customFacts, faq }),
  });

export const loadBrandless = ({ request, accountId, query, limit = 50, offset = 0 }) => {
  const params = new URLSearchParams({ account_id: accountId, limit: String(limit), offset: String(offset) });
  if (query) params.set("q", query);
  return request(`/api/v1/feedback/kb/brandless?${params.toString()}`);
};

export const saveBrands = ({ request, accountId, pairs }) =>
  request(`/api/v1/feedback/kb/brands?${base(accountId)}`, { method: "PUT", body: JSON.stringify({ pairs }) });

// Совместимости: связки товаров для ответов ИИ
const compatBase = () => `/api/v1/feedback/kb/compat`;

export const loadCompatMeta = ({ request, accountId }) => request(`${compatBase()}/meta?${base(accountId)}`);

export const loadCompatLinks = ({ request, accountId, query, limit = 50, offset = 0 }) => {
  const params = new URLSearchParams({ account_id: accountId, limit: String(limit), offset: String(offset) });
  if (query) params.set('q', query);
  return request(`${compatBase()}/links?${params.toString()}`);
};

export const saveCompatLink = ({ request, accountId, id, link }) =>
  request(`${compatBase()}/links${id ? `/${id}` : ''}?${base(accountId)}`, {
    method: id ? 'PUT' : 'POST',
    body: JSON.stringify(link),
  });

export const deleteCompatLink = ({ request, accountId, id }) =>
  request(`${compatBase()}/links/${id}?${base(accountId)}`, { method: 'DELETE' });

export const createCompatType = ({ request, accountId, name, description }) =>
  request(`${compatBase()}/types?${base(accountId)}`, { method: 'POST', body: JSON.stringify({ name, description }) });

export const deleteCompatType = ({ request, accountId, id }) =>
  request(`${compatBase()}/types/${id}?${base(accountId)}`, { method: 'DELETE' });

export const downloadCompatTemplate = ({ fetchBlob, accountId }) =>
  fetchBlob(`${compatBase()}/template?${base(accountId)}`);

export const importCompat = ({ request, accountId, file }) => {
  const form = new FormData();
  form.append('file', file);
  return request(`${compatBase()}/import?${base(accountId)}`, { method: 'POST', body: form });
};

// Отчёты по отзывам для руководителя. params: { skus: string[]|null, period: { days } | { from, to } }
const reportBody = ({ skus, period }) => JSON.stringify({
  skus: skus && skus.length ? skus : null,
  ...(period && period.from && period.to ? { date_from: period.from, date_to: period.to } : { days: (period && period.days) || 90 }),
});

export const estimateFeedbackReport = ({ request, accountId, skus, period }) =>
  request(`/api/v1/feedback/reports/estimate?${base(accountId)}`, { method: 'POST', body: reportBody({ skus, period }) });

export const createFeedbackReport = ({ request, accountId, skus, period }) =>
  request(`/api/v1/feedback/reports?${base(accountId)}`, { method: 'POST', body: reportBody({ skus, period }) });

export const listFeedbackReports = ({ request, accountId }) =>
  request(`/api/v1/feedback/reports?${base(accountId)}`);

export const getFeedbackReport = ({ request, reportId }) =>
  request(`/api/v1/feedback/reports/${reportId}`);

// Вопросы и чаты: группировка по артикулам или моделям, ответы для базы знаний отдельными шагами.
// period: { days } | { from, to }; level: 'sku' | 'model'
const faqBody = ({ period, level, includeGaps }) => JSON.stringify({
  ...(period && period.from && period.to ? { date_from: period.from, date_to: period.to } : { days: (period && period.days) || 90 }),
  level: level || 'sku',
  include_gaps: Boolean(includeGaps),
});

export const estimateFaq = ({ request, accountId, ...options }) =>
  request(`/api/v1/feedback/faq/estimate?${base(accountId)}`, { method: 'POST', body: faqBody(options) });

export const runFaq = ({ request, accountId, ...options }) =>
  request(`/api/v1/feedback/faq/run?${base(accountId)}`, { method: 'POST', body: faqBody(options) });

export const listFaqRuns = ({ request, accountId }) =>
  request(`/api/v1/feedback/faq/runs?${base(accountId)}`);

export const getFaqRun = ({ request, runId }) =>
  request(`/api/v1/feedback/faq/runs/${runId}`);

const idsBody = (entryIds) => JSON.stringify({ entry_ids: entryIds && entryIds.length ? entryIds : null });

export const estimateFaqAnswers = ({ request, runId, entryIds }) =>
  request(`/api/v1/feedback/faq/runs/${runId}/answers/estimate`, { method: 'POST', body: idsBody(entryIds) });

export const generateFaqAnswers = ({ request, runId, entryIds }) =>
  request(`/api/v1/feedback/faq/runs/${runId}/answers`, { method: 'POST', body: idsBody(entryIds) });

export const updateFaqEntry = ({ request, runId, entryId, question, answer }) =>
  request(`/api/v1/feedback/faq/runs/${runId}/entries/${entryId}`, {
    method: 'PUT',
    body: JSON.stringify({ ...(question !== undefined ? { question } : {}), ...(answer !== undefined ? { answer } : {}) }),
  });

export const sendFaqToKnowledge = ({ request, runId, entryIds }) =>
  request(`/api/v1/feedback/faq/runs/${runId}/send`, { method: 'POST', body: idsBody(entryIds) });
